import { useState } from "react";
import { Link } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus,
  DollarSign,
  ShoppingCart,
  Users,
  Target,
  ArrowRight,
  Search,
  Filter,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { AnalyticsFreshnessBanner } from "@/components/admin/shared/AnalyticsFreshnessBanner";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { analyticsApi } from "@/lib/admin-api/analytics";
import type {
  AnalyticsOverviewResponse,
  AnalyticsDelta,
} from "@/lib/admin-api/analytics";

// ---------------------------------------------------------------------------
// Period selector
// ---------------------------------------------------------------------------

type Period = "7d" | "30d" | "90d";

const PERIOD_OPTIONS: readonly { readonly value: Period; readonly label: string }[] = [
  { value: "7d", label: "7 jours" },
  { value: "30d", label: "30 jours" },
  { value: "90d", label: "90 jours" },
];

function PeriodSelector({
  value,
  onChange,
}: {
  readonly value: Period;
  readonly onChange: (p: Period) => void;
}) {
  return (
    <div className="flex gap-1.5">
      {PERIOD_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            value === opt.value
              ? "bg-primary text-primary-foreground"
              : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FCFA formatter
// ---------------------------------------------------------------------------

import { formatFcfa } from "@/lib/format";

function formatNumber(n: number): string {
  return new Intl.NumberFormat("fr-FR").format(n);
}

function formatPercent(n: number): string {
  return (
    new Intl.NumberFormat("fr-FR", {
      style: "decimal",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(n) + "%"
  );
}

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------

interface KpiCardProps {
  readonly label: string;
  readonly value: string;
  readonly changePct: number;
  readonly direction: "up" | "down" | "flat";
  readonly icon: React.ElementType;
  readonly iconColor: string;
}

function KpiCard({ label, value, changePct, direction, icon: Icon, iconColor }: KpiCardProps) {
  const DirectionIcon =
    direction === "up" ? TrendingUp :
    direction === "down" ? TrendingDown :
    Minus;

  const directionColor =
    direction === "up" ? "text-emerald-600 dark:text-emerald-400" :
    direction === "down" ? "text-red-600 dark:text-red-400" :
    "text-muted-foreground";

  return (
    <div className="bg-card rounded-xl border border-border p-5 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        <div className={`p-2 rounded-lg ${iconColor}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="space-y-1">
        <p className="text-2xl font-bold text-foreground tracking-tight">{value}</p>
        <div className="flex items-center gap-1.5">
          <DirectionIcon className={`h-3.5 w-3.5 ${directionColor}`} />
          <span className={`text-xs font-medium ${directionColor}`}>
            {changePct >= 0 ? "+" : ""}
            {formatPercent(changePct)}
          </span>
          <span className="text-xs text-muted-foreground">vs periode precedente</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI skeleton
// ---------------------------------------------------------------------------

function KpiSkeleton() {
  return (
    <div className="bg-card rounded-xl border border-border p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="h-4 w-24 animate-pulse rounded bg-muted" />
        <div className="h-8 w-8 animate-pulse rounded-lg bg-muted/40" />
      </div>
      <div className="space-y-2">
        <div className="h-7 w-32 animate-pulse rounded bg-muted" />
        <div className="h-3 w-20 animate-pulse rounded bg-muted/60" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-page link cards
// ---------------------------------------------------------------------------

const ANALYTICS_SECTIONS = [
  {
    title: "Tendances produits",
    description: "Produits les plus populaires par vues, ajouts panier et achats.",
    href: "/admin/analytics/trending",
    icon: TrendingUp,
    color: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  },
  {
    title: "Recherches",
    description: "Termes recherches, taux de clic et recherches sans resultats.",
    href: "/admin/analytics/search",
    icon: Search,
    color: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  },
  {
    title: "Entonnoir de conversion",
    description: "Parcours visiteur du premier clic jusqu'a l'achat.",
    href: "/admin/analytics/funnel",
    icon: Filter,
    color: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  },
  {
    title: "Intelligence de recherche",
    description: "Intentions d'achat, recherches zero-resultat et produits favoris.",
    href: "/admin/analytics/intent",
    icon: Sparkles,
    color: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  },
] as const;

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function getDelta(
  delta: AnalyticsDelta | undefined,
  field: string,
): { changePct: number; direction: "up" | "down" | "flat" } {
  const entry = delta?.[field];
  if (!entry || typeof entry !== "object") return { changePct: 0, direction: "flat" };
  return { changePct: entry.pct, direction: entry.direction };
}

export default function Overview() {
  useAdminPageTitle("Analytique");
  const [period, setPeriod] = useState<Period>("30d");

  const { data, isLoading, error, refetch } = useQuery<AnalyticsOverviewResponse>({
    queryKey: ["analytics-overview", period],
    queryFn: () => analyticsApi.overview(period),
  });

  const metrics = data?.current?.metrics;
  const delta = data?.delta;

  return (
    <AdminLayout>
      <div className="space-y-8">
        <AnalyticsFreshnessBanner />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground flex items-center gap-2.5">
              <BarChart3 className="h-6 w-6 text-primary" />
              Analytique
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Vue d'ensemble des performances commerciales.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <PeriodSelector value={period} onChange={setPeriod} />
            <button
              type="button"
              onClick={() => refetch()}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              aria-label="Actualiser"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 text-sm text-destructive">
            Erreur lors du chargement des données. Veuillez réessayer.
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {isLoading ? (
            Array.from({ length: 4 }, (_, i) => <KpiSkeleton key={i} />)
          ) : metrics ? (
            <>
              <KpiCard
                label="Chiffre d'affaires"
                value={formatFcfa(metrics.revenue_ttc_fcfa)}
                {...getDelta(delta, "revenue_ttc_fcfa")}
                icon={DollarSign}
                iconColor="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              />
              <KpiCard
                label="Commandes"
                value={formatNumber(metrics.orders_count)}
                {...getDelta(delta, "orders_count")}
                icon={ShoppingCart}
                iconColor="bg-blue-500/10 text-blue-600 dark:text-blue-400"
              />
              <KpiCard
                label="Clients actifs"
                value={formatNumber(metrics.unique_customers)}
                {...getDelta(delta, "unique_customers")}
                icon={Users}
                iconColor="bg-violet-500/10 text-violet-600 dark:text-violet-400"
              />
              <KpiCard
                label="Taux de conversion"
                value={formatPercent(metrics.overall_conversion_rate)}
                {...getDelta(delta, "overall_conversion_rate")}
                icon={Target}
                iconColor="bg-amber-500/10 text-amber-600 dark:text-amber-400"
              />
            </>
          ) : null}
        </div>

        {/* Sub-section cards */}
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4">Explorer</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ANALYTICS_SECTIONS.map((section) => {
              const SectionIcon = section.icon;
              return (
                <Link
                  key={section.href}
                  to={section.href}
                  className="group flex flex-col gap-3 rounded-xl border border-border bg-card p-5 transition-colors hover:bg-muted/30"
                >
                  <div className={`p-2.5 rounded-lg w-fit ${section.color}`}>
                    <SectionIcon className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-semibold text-foreground text-sm group-hover:text-primary transition-colors">
                      {section.title}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {section.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground group-hover:text-primary transition-colors mt-auto pt-1">
                    <span>Voir</span>
                    <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
